import { Injectable } from '@angular/core';
import { ExecutionStatus } from '@cdw-ae/common-data';
import {
  selectJobExecutionByCorrelationId,
  selectJobExecutionByJobId,
} from '@cdw-ae/jobs-status-state';
import { Store } from '@ngrx/store';
import {
  catchError,
  EMPTY,
  filter,
  map,
  Observable,
  take,
  tap,
  timeout,
  TimeoutError,
} from 'rxjs';

type CorrelationSelector = ReturnType<typeof selectJobExecutionByCorrelationId>;
type BulkJobSelector = ReturnType<typeof selectJobExecutionByJobId>;

// 60s for "job picked up" — dispatch should be near-instant; if Firestore
// hasn't propagated a non-pending status by 60s the job is effectively lost.
const JOB_STARTED_TIMEOUT_MS = 60_000;
// 4 hours for "job completed" — this is now a hard backstop, NOT the primary
// stuck-state recovery. The SSRM selection-lock failsafe (fix/SSRM-row-activity-ui)
// owns user-visible stuck-state UX with per-job-size budgets:
//   threshold_ms = 600_000 + (selectedCount / 5000) * 60_000
//   = 10 min + 1 min per 5k assets
// At the documented 1M-asset upper bound that's 210 min (~3.5 hours). Setting
// this constant to 240 min (4h) keeps it strictly outside the failsafe's
// ceiling so a legitimately long job never trips both. Hitting this timeout
// implies "something has gone catastrophically wrong" — the worker is gone,
// the selector is leaking, etc. — and the consumer's onError callback should
// surface a hard failure.
const JOB_COMPLETION_TIMEOUT_MS = 4 * 60 * 60_000;

/**
 * Service for monitoring async job execution via Firestore
 * Simplifies component code by encapsulating NgRx subscription patterns
 */
@Injectable({
  providedIn: 'root',
})
export class JobMonitorService {
  private readonly correlationSelectors = new Map<
    string,
    CorrelationSelector
  >();
  private readonly bulkJobSelectors = new Map<number, BulkJobSelector>();

  constructor(private store: Store) {}

  private getCorrelationSelector(correlationId: string): CorrelationSelector {
    let selector = this.correlationSelectors.get(correlationId);
    if (!selector) {
      selector = selectJobExecutionByCorrelationId(correlationId);
      this.correlationSelectors.set(correlationId, selector);
    }
    return selector;
  }

  private getBulkJobSelector(jobId: number): BulkJobSelector {
    let selector = this.bulkJobSelectors.get(jobId);
    if (!selector) {
      selector = selectJobExecutionByJobId(jobId);
      this.bulkJobSelectors.set(jobId, selector);
    }
    return selector;
  }

  /**
   * Monitor a job until it completes (finished or error)
   * Jobs are automatically tracked via bindToMyJobs/bindToAllJobs effects
   *
   * @param customerId - Customer ID (unused but kept for API compatibility)
   * @param jobId - Job ID to monitor
   * @returns Observable that emits once when job completes, then completes
   *
   * @example
   * ```typescript
   * this.jobMonitor.monitorJobCompletion(customerId, jobId)
   *   .pipe(takeUntil(this.destroy$))
   *   .subscribe(status => {
   *     if (status === ExecutionStatus.finished) {
   *       this.refreshGrid();
   *     } else {
   *       this.showError();
   *     }
   *   });
   * ```
   */
  monitorJobCompletion(
    customerId: number,
    jobId: number
  ): Observable<ExecutionStatus> {
    return this.store.select(this.getBulkJobSelector(jobId)).pipe(
      // Wait for execution to exist
      filter(
        (execution): execution is NonNullable<typeof execution> => !!execution
      ),
      // Extract status
      map((execution) => execution.executionStatus),
      // Wait for completion (finished, failed, error, or cancelled)
      filter(
        (status) =>
          status === ExecutionStatus.finished ||
          status === ExecutionStatus.failed ||
          status === ExecutionStatus.error ||
          status === ExecutionStatus.cancelledRunning ||
          status === ExecutionStatus.cancelledPending
      ),
      // Auto-complete after first completion event
      take(1),
      timeout({ first: JOB_COMPLETION_TIMEOUT_MS }),
      // Clean up cached selector
      tap(() => {
        this.bulkJobSelectors.delete(jobId);
      })
    );
  }

  /**
   * Monitor a job and automatically refresh a callback when complete
   * Convenience method that handles success/error cases
   *
   * @param customerId - Customer ID for Firestore binding
   * @param jobId - Job ID to monitor
   * @param onSuccess - Callback when job finishes successfully
   * @param onError - Optional callback when job errors
   * @returns Observable for subscription management
   *
   * @example
   * ```typescript
   * this.jobMonitor.monitorWithCallbacks(
   *   customerId,
   *   jobId,
   *   () => this.refreshGrid(),
   *   () => this.snackbar.showError('Job failed')
   * ).pipe(takeUntil(this.destroy$)).subscribe();
   * ```
   */
  monitorWithCallbacks(
    customerId: number,
    jobId: number,
    onSuccess: () => void,
    onError?: (error?: unknown) => void
  ): Observable<void> {
    return this.monitorJobCompletion(customerId, jobId).pipe(
      tap((status) => {
        if (status === ExecutionStatus.finished) {
          onSuccess();
        } else if (onError) {
          onError();
        }
      }),
      catchError((error) => {
        // TimeoutError or any other unexpected error — route through
        // onError so the consumer's spinner/UI state can recover. Without
        // this, a stuck job leaves the UI hung indefinitely.
        if (onError) onError(error);
        if (error instanceof TimeoutError) {
          this.bulkJobSelectors.delete(jobId);
        }
        return EMPTY;
      }),
      map(() => void 0) // Return void for cleaner subscription
    );
  }

  /**
   * monitor a job until it transitions OUT of
   * `pending` for the first time. Useful when the UI needs to react the
   * moment the worker picks up the job (e.g., flipping an in-flight save
   * UI from "Saving..." to "show per-cell spinners + exit edit mode").
   *
   * Emits exactly once on the first non-pending status (`started` is the
   * common case, but if the worker is fast and the snapshot we observe is
   * already `finished`/`failed`/`error`, those count too — the consumer
   * still wants the "no longer queued" signal). Then completes.
   *
   * @param customerId - Customer ID (unused, kept for API symmetry)
   * @param jobId - Job ID to monitor
   * @returns Observable that emits once with the first non-pending status
   *
   * @example
   * ```typescript
   * this.jobMonitor.monitorJobStarted(customerId, jobId)
   *   .pipe(takeUntil(this.destroy$))
   *   .subscribe(() => {
   *     this.isSaving = false;
   *     this.exitEditMode();
   *   });
   * ```
   */
  monitorJobStarted(
    customerId: number,
    jobId: number
  ): Observable<ExecutionStatus> {
    return this.store.select(this.getBulkJobSelector(jobId)).pipe(
      filter(
        (execution): execution is NonNullable<typeof execution> => !!execution
      ),
      map((execution) => execution.executionStatus),
      filter((status) => status !== ExecutionStatus.pending),
      take(1),
      timeout({ first: JOB_STARTED_TIMEOUT_MS })
      // No selector cleanup here — the completion monitor (started by the
      // same consumer call site) will run AFTER this and needs the cached
      // selector still in place.
    );
  }

  /**
   * Monitor a job by its client-generated correlationId until it completes
   * Jobs are automatically tracked via bindToMyJobs/bindToAllJobs effects
   *
   * @param customerId - Customer ID (unused but kept for API compatibility)
   * @param correlationId - Client-generated UUID stored in localStorage
   * @returns Observable that emits once when job completes, then completes
   */
  monitorByCorrelationId(
    customerId: number,
    correlationId: string
  ): Observable<ExecutionStatus> {
    return this.store.select(this.getCorrelationSelector(correlationId)).pipe(
      filter(
        (execution): execution is NonNullable<typeof execution> => !!execution
      ),
      map((execution) => execution.executionStatus),
      filter(
        (status) =>
          status === ExecutionStatus.finished ||
          status === ExecutionStatus.failed ||
          status === ExecutionStatus.error ||
          status === ExecutionStatus.cancelledRunning ||
          status === ExecutionStatus.cancelledPending
      ),
      take(1),
      timeout({ first: JOB_COMPLETION_TIMEOUT_MS }),
      tap(() => {
        this.correlationSelectors.delete(correlationId);
      })
    );
  }

  /**
   * Monitor a job by correlationId with success/error callbacks
   * Convenience method for the common pattern of react-to-completion
   */
  monitorByCorrelationIdWithCallbacks(
    customerId: number,
    correlationId: string,
    onSuccess: () => void,
    onError?: (error?: unknown) => void
  ): Observable<void> {
    return this.monitorByCorrelationId(customerId, correlationId).pipe(
      tap((status) => {
        if (status === ExecutionStatus.finished) {
          onSuccess();
        } else if (onError) {
          onError();
        }
      }),
      catchError((error) => {
        if (onError) onError(error);
        if (error instanceof TimeoutError) {
          this.correlationSelectors.delete(correlationId);
        }
        return EMPTY;
      }),
      map(() => void 0)
    );
  }
}
